import { useCallback, useMemo } from 'react';
import { useCreateOrganizationTicket } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';
import {
  TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES,
  TICKET_MAX_FILES_NUMBER,
  TICKET_MAX_SIZE_MB,
} from '@sib/config';
import { useNotification } from '@sib/notifications';
import { FileStatus } from '@sib/types';
import { useBaseDialogForm } from '@spendbrain/common';
import { useDialogStore } from '@spendbrain/common/stores';
import { useAppTranslation } from '@spendbrain/modules/tickets/hooks';
import { useTicketFileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { z } from 'zod';

export const TICKET_SUBJECT_MAX_LENGTH = 256;
export const TICKET_DESCRIPTION_MAX_LENGTH = 1024;

export enum TicketFormField {
  Subject = 'subject',
  Description = 'description',
}

export const useCreateTicketFormValidationSchema = () => {
  const { t } = useAppTranslation();

  return z.object({
    [TicketFormField.Subject]: z
      .string()
      .min(1, {
        message: t('validation.required'),
      })
      .max(TICKET_SUBJECT_MAX_LENGTH, {
        message: t('validation.messageCantBeLongerThan', {
          max: TICKET_SUBJECT_MAX_LENGTH,
        }),
      }),

    [TicketFormField.Description]: z
      .string()
      .max(TICKET_DESCRIPTION_MAX_LENGTH, {
        message: t('validation.messageCantBeLongerThan', {
          max: TICKET_DESCRIPTION_MAX_LENGTH,
        }),
      })
      .optional(),
  });
};

export type TicketFormValues = z.infer<
  ReturnType<typeof useCreateTicketFormValidationSchema>
>;

const useTicketFormDefaultValues = () => ({
  [TicketFormField.Subject]: '',
  [TicketFormField.Description]: '',
});

export const useCreateTicketForm = () => {
  const { t } = useAppTranslation();
  const validationSchema = useCreateTicketFormValidationSchema();
  const { resetFiles, files, updateFile } = useTicketFileUploadStore();
  const { setClosable } = useDialogStore();
  const organizationId = useUserSetupStore(state => state.organization.id);
  const { open } = useNotification();
  const defaultValues = useTicketFormDefaultValues();

  const checkMaxFilesError = useCallback(() => {
    if (files.length > TICKET_MAX_FILES_NUMBER) {
      return t('components.createTicketForm.errors.maxFiles', {
        maxFiles: TICKET_MAX_FILES_NUMBER,
      });
    }

    return null;
  }, [files, t]);

  const checkMaxSizeError = useCallback(() => {
    const totalSize = files.reduce(
      (acc, fileItem) => acc + fileItem.file.size,
      0
    );

    if (totalSize > TICKET_ATTACHMENTS_DROPZONE_MAX_SIZE_BYTES) {
      return t('components.createTicketForm.errors.maxSize', {
        maxSize: TICKET_MAX_SIZE_MB,
      });
    }

    return null;
  }, [files, t]);

  const filesErrors = useMemo(
    () => [checkMaxFilesError(), checkMaxSizeError()].filter(Boolean),
    [checkMaxFilesError, checkMaxSizeError]
  );

  const { mutateAsync: createTicket } = useCreateOrganizationTicket({
    options: {
      onSuccess: () => {
        onFormClose();
        open({
          type: 'success',
          message: t('components.createTicketForm.success.message'),
        });
      },
      onError: () => {
        open({
          type: 'error',
          message: t('components.createTicketForm.error.message'),
        });
      },
    },
  });

  const onFormSubmit = async (values: TicketFormValues) => {
    const filesToUpload = files.map(fileItem => fileItem.file);
    files.forEach(fileItem => {
      updateFile({
        ...fileItem,
        status: FileStatus.Uploading,
      });
    });

    try {
      await createTicket({
        ...values,
        files: filesToUpload,
        organizationId,
      });
    } catch {
      files.forEach(fileItem => {
        updateFile({
          ...fileItem,
          status: FileStatus.Pending,
        });
      });
    } finally {
      setClosable(true);
    }
  };

  const { formProps, onSubmit, onFormClose } = useBaseDialogForm({
    onSubmit: onFormSubmit,
    defaultValues,
    validationSchema,
    onClose: () => {
      resetFiles();
    },
  });

  return {
    formProps,
    onCancel: onFormClose,
    onSubmit,
    filesErrors,
  };
};
